import { FC } from 'react'

import {
  Text,
  IconCircleInfo,
  Button,
  GreyGrey60,
  GreyGrey80,
  Popover,
  Primary12SemiBold,
  TextAreaFieldInput,
  TextFieldInput,
  Flex
} from '@sequencehq/core-components'
import { useAtom, useAtomValue } from 'jotai'
import { usePermissions } from 'lib/rbac'
import {
  automationDescriptionAtom,
  automationNameAtom,
  versionPreviewAtom
} from 'Automations/automationEditor/atoms'
import { UNTITLED_AUTOMATION_NAME } from 'Automations/automationEditor/constants'

export const WorkflowDetails: FC = () => {
  const { can } = usePermissions()
  const savePublishButtonsVisible = can('org.watchtower:update')

  return (
    <Popover
      variant="standard-v2"
      content={<Content />}
      trigger="click"
      gutter={12}
      showArrow={false}
      maxWidth="340px"
      /**
       * Change the popover alignment when save/publish buttons are hidden
       * to avoid attempting to centre the popover close to the viewport edge
       */
      position={savePublishButtonsVisible ? 'bottom' : 'bottom-end'}
    >
      <Trigger />
    </Popover>
  )
}

const Content: FC = () => {
  const { can } = usePermissions()

  const [name, setName] = useAtom(automationNameAtom)
  const [description, setDescription] = useAtom(automationDescriptionAtom)

  const fieldsDisabled = !can('org.watchtower:update')

  return (
    <Flex flexDir="column" width="324px">
      <Flex flexDir="column" gap="8px">
        <Flex flexDir="column" gap="4px">
          <Text {...Primary12SemiBold} color={GreyGrey60}>
            Name
          </Text>
          <TextFieldInput
            data-testid="automations.editor.workflowDetails.name"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={UNTITLED_AUTOMATION_NAME}
            isDisabled={fieldsDisabled}
          />
        </Flex>

        <Flex flexDir="column" gap="4px">
          <Text {...Primary12SemiBold} color={GreyGrey60}>
            Description
          </Text>
          <TextAreaFieldInput
            data-testid="automations.editor.workflowDetails.description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Workflow description"
            isDisabled={fieldsDisabled}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}

const Trigger: FC = () => {
  const versionPreview = useAtomValue(versionPreviewAtom)
  const isPreviewingVersion = Boolean(versionPreview)

  return (
    <Button
      variant="ghost"
      data-testid="automations.editor.workflowDetails"
      icon={<IconCircleInfo color={GreyGrey80} />}
      aria-label="Workflow details"
      disabled={isPreviewingVersion}
      style={{ opacity: isPreviewingVersion ? 0.5 : 1 }}
    />
  )
}
